import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { submitSubscribeForm } from "./form.actions";
import {
  animateNewsletterSection,
  animateFormState,
  animateSuccessMessage,
  animateErrorMessage,
  cleanupScrollTriggers,
} from "./gsap";
import { SubscribeFormData, SubscribeFormState, FormValidation } from "./types";

const SubscribeForm: React.FC = () => {
  const [email, setEmail] = useState("");
  const [formState, setFormState] = useState<SubscribeFormState>({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    message: "",
  });

  const sectionRef = useRef<HTMLElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const successMessageRef = useRef<HTMLDivElement>(null);
  const errorMessageRef = useRef<HTMLDivElement>(null);
  const submitTimeoutRef = useRef<NodeJS.Timeout>();

  // Memoized email validation
  const emailValidation = useMemo((): FormValidation => {
    if (!email.trim()) {
      return { isValid: false, errors: [] };
    }

    const errors: string[] = [];
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    if (!emailRegex.test(email.trim())) {
      errors.push("Please enter a valid email address");
    }

    if (email.length > 254) {
      errors.push("Email address is too long");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [email]);

  // Set up animations on mount
  useEffect(() => {
    const animationTimeout = setTimeout(() => {
      animateNewsletterSection(sectionRef);
    }, 100);

    return () => {
      clearTimeout(animationTimeout);
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
      cleanupScrollTriggers();
    };
  }, []);

  // Animate form state changes
  useEffect(() => {
    if (formState.isSubmitting) {
      animateFormState(formRef, "submitting");
    } else if (formState.isSuccess) {
      animateFormState(formRef, "success");
    } else if (formState.isError) {
      animateFormState(formRef, "error");
    } else {
      animateFormState(formRef, "idle");
    }
  }, [formState.isSubmitting, formState.isSuccess, formState.isError]);

  // Animate success message
  useEffect(() => {
    if (formState.isSuccess && successMessageRef.current) {
      animateSuccessMessage(successMessageRef);
    }
  }, [formState.isSuccess]);

  // Animate error message
  useEffect(() => {
    if (formState.isError && errorMessageRef.current) {
      animateErrorMessage(errorMessageRef);
    }
  }, [formState.isError]);

  // Optimized email change handler
  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newEmail = e.target.value;
      setEmail(newEmail);

      // Clear errors when user starts typing
      if (formState.isError && newEmail.trim()) {
        setFormState((prev) => ({
          ...prev,
          isError: false,
          message: "",
        }));
      }
    },
    [formState.isError]
  );

  // Enhanced form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Clear any existing timeout
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }

      // Reset form state
      setFormState({
        isSubmitting: false,
        isSuccess: false,
        isError: false,
        message: "",
      });

      // Validate email
      if (!emailValidation.isValid) {
        setFormState({
          isSubmitting: false,
          isSuccess: false,
          isError: true,
          message:
            emailValidation.errors[0] || "Please enter a valid email address",
        });
        return;
      }

      setFormState((prev) => ({ ...prev, isSubmitting: true }));

      try {
        const formData: SubscribeFormData = {
          email: email.trim().toLowerCase(),
          source: "homepage_cta",
        };

        const response = await submitSubscribeForm(formData);

        if (response.success) {
          setFormState({
            isSubmitting: false,
            isSuccess: true,
            isError: false,
            message: response.message,
          });

          // Clear email field after successful submission
          setEmail("");

          // Auto-reset success state after 5 seconds
          submitTimeoutRef.current = setTimeout(() => {
            setFormState((prev) => ({
              ...prev,
              isSuccess: false,
              message: "",
            }));
          }, 5000);
        } else {
          setFormState({
            isSubmitting: false,
            isSuccess: false,
            isError: true,
            message: response.message,
          });
        }
      } catch (error) {
        setFormState({
          isSubmitting: false,
          isSuccess: false,
          isError: true,
          message: "An unexpected error occurred. Please try again later.",
        });
      }
    },
    [email, emailValidation]
  );

  // Determine button state
  const getButtonContent = () => {
    if (formState.isSubmitting) {
      return (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Subscribing...
        </>
      );
    }

    if (formState.isSuccess) {
      return (
        <>
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
          Subscribed!
        </>
      );
    }

    return "Subscribe";
  };

  return (
    <section
      ref={sectionRef}
      className="py-16 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 text-white relative overflow-hidden"
      aria-labelledby="newsletter-heading"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-r from-cannabis-green-900/10 to-transparent opacity-50" />

      <div className="container mx-auto px-4 text-center relative z-10">
        <h2
          id="newsletter-heading"
          className="text-3xl md:text-4xl font-bold mb-4 text-white"
        >
          Join Our Community
        </h2>
        <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-300 leading-relaxed">
          Stay updated with our latest products, exclusive promotions, and
          community events.
        </p>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="max-w-md mx-auto"
          noValidate
        >
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-2">
            <div className="flex-grow">
              <label htmlFor="newsletter-email" className="sr-only">
                Email address
              </label>
              <input
                id="newsletter-email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter your email address"
                className={`w-full px-4 py-3 rounded-lg text-gray-900 bg-white border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cannabis-green-400 focus:border-cannabis-green-400 ${
                  formState.isError && !emailValidation.isValid
                    ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                    : "border-gray-300"
                }`}
                disabled={formState.isSubmitting || formState.isSuccess}
                autoComplete="email"
                aria-describedby={
                  formState.isError ? "newsletter-error" : undefined
                }
                aria-invalid={formState.isError && !emailValidation.isValid}
              />
            </div>
            <button
              type="submit"
              className={`px-6 py-3 font-medium rounded-lg transition-all duration-300 flex items-center justify-center min-w-[140px] ${
                formState.isSuccess
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-cannabis-green-500 hover:bg-cannabis-green-600 text-white hover:shadow-lg transform hover:scale-105"
              }`}
              disabled={
                formState.isSubmitting ||
                !emailValidation.isValid ||
                formState.isSuccess
              }
              aria-describedby="button-status"
            >
              {getButtonContent()}
            </button>
          </div>

          {/* Status messages */}
          {formState.isSuccess && (
            <div
              ref={successMessageRef}
              className="mt-4 text-cannabis-green-300 text-sm font-medium"
              role="status"
              aria-live="polite"
            >
              {formState.message}
            </div>
          )}

          {formState.isError && (
            <div
              ref={errorMessageRef}
              id="newsletter-error"
              className="mt-4 text-red-400 text-sm font-medium"
              role="alert"
              aria-live="assertive"
            >
              {formState.message}
            </div>
          )}

          <div className="mt-4 text-xs text-gray-400">
            By subscribing, you agree to our{" "}
            <a
              href="/privacy-policy"
              className="underline hover:text-white transition-colors duration-200"
              target="_blank"
              rel="noopener"
            >
              Privacy Policy
            </a>
            . Unsubscribe at any time.
          </div>
        </form>
      </div>
    </section>
  );
};

export default React.memo(SubscribeForm);
